import {ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, ViewChild} from '@angular/core';
import {Chart} from "chart.js/auto";
import {Subscription} from "rxjs";
import {PlatformScannerService} from "../../../../../../_services/platform-scanner.service";
import {ActiveElement, ChartEvent} from "chart.js";
import {GenericSortingService} from "../../../../../../_services/generic-sorting.service";
import {Platform} from "../../../../../../enums/platform";
import {ErrorCodes} from "../../../../common/enums/states/error-codes";
import {TimeSeriesPerformanceStoreService} from "../../../store/time-series-performance-store.service";
import {RevenueBreakdownStore} from '../../../store/revenueBreakdown/revenue-breakdown-store';
import {PercentUiControllerService} from "./services/percent-ui-controller.service";
import {HoverService} from "./services/hover.service";
import {CommonModule} from "@angular/common";
import {ErrorMessageComponent} from "../../../../common/components/error-message/error-message.component";

@Component({
  selector: 'analytics-revenue-doughnut-chart',
  templateUrl: './revenue-doughnut-chart.component.html',
  styleUrls: ['./revenue-doughnut-chart.component.css'],
  imports: [
    CommonModule,
    ErrorMessageComponent
  ]
})
export class RevenueDoughnutChartComponent implements OnDestroy {
  chart?: Chart<"doughnut", any, any>;
  selectedIndex: number | undefined = undefined;
  platform = this.platformScannerService.getPlatform();
  hoverId!: string
  selfIdentifier: string = "revenueDoughnutChart";
  subscription: Subscription = new Subscription();
  protected readonly ErrorAnalysis = ErrorCodes;
  protected readonly undefined = undefined;
  private chartElement?: ElementRef<HTMLCanvasElement>;

  constructor(private cd: ChangeDetectorRef,
              private platformScannerService: PlatformScannerService,
              private hoverService: HoverService,
              private percentUiControllerService: PercentUiControllerService,
              public timeSeriesPerformanceStore: TimeSeriesPerformanceStoreService,
              public revenueBreakdownStore: RevenueBreakdownStore
  ) {
    this.subscribe()
  }

  @ViewChild('revenueDoughnutChart', {static: false}) set content(content: ElementRef<HTMLCanvasElement>) {
    this.chartElement = content;
    //this.createChart();
  }

  @Input("hoverModel") set hoverInput(hoverModel: { id: string, selfIdentifier: string }) {
    this.hoverId = hoverModel.id;
    this.selfIdentifier = hoverModel.selfIdentifier;
    this.subscribeToHoverService();
  }

  static stringToCurrency(value: string): string {
    return new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'}).format(Number(value))
  }

  ngOnDestroy() {
    this.chart?.destroy();
    this.subscription.unsubscribe();
  }

  subscribeToHoverService() {
    this.subscription.add(this.hoverService.getEmitter(this.hoverId).subscribe((x: {
      datasetIndex: number,
      index: number,
      trigger: string
    }) => {
      if (x.trigger === this.selfIdentifier) return;
      this.triggerHover(x.index);
    }));

  }

  subscribe() {
    this.subscription.add(this.revenueBreakdownStore.displayOptionChangedNotifier.subscribe(
      () => {
        this.createChart();
      }
    ))
  }

  triggerHover(index: number) {
    this.selectedIndex = index >= 0 ? index : undefined;

    if (this.selectedIndex === undefined) {

      this.chart?.setActiveElements([]);
      this.chart?.update();
      return;
    }

    this.chart?.setActiveElements([{datasetIndex: 0, index}]);
    this.chart?.update();
  }

  createChart() {
    if (this.chart !== undefined) {
      this.chart?.destroy()
    }


    if (this.revenueBreakdownStore.getPieChart() === undefined || this.revenueBreakdownStore.getPieChart().datasets.length === 0 || this.revenueBreakdownStore.getPieChart().datasets[0].data.length === 0) {
      return
    }

    //this.displayNoDataAvailableMessage(undefined)

    if (this.chartElement === undefined) return;

    this.ascendingSort()

    //let labels= this.data.labels as string[]

    //let chartIsDateBundled = this.resortRequired();

    this.chart = new Chart<"doughnut", any, any>(this.chartElement?.nativeElement, {
        type: 'doughnut',
        data: this.revenueBreakdownStore.getPieChart(),
        options: {
          maintainAspectRatio: true,
          responsive: true,
          aspectRatio: 0,
          cutout: '75%',
          hover: {intersect: true},
          // @ts-ignore
          hoverOffset: 22,
          layout: {
            padding: {
              bottom: 25,
              top: 30
            }
          },
          onHover: (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {

            this.selectedIndex = elements.length === 0 ? undefined : elements[0].index;
            chart.canvas.style.cursor = elements.length === 0 ? 'default' : 'pointer';
            this.hoverService.getEmitter(this.hoverId).emit({
              datasetIndex: 0,
              index: this.selectedIndex ?? -1,
              trigger: this.selfIdentifier
            });
          },
          plugins: {
            tooltip: {
              enabled: false,
            },
            legend: {
              labels: {},
              onClick: (e, legendItem, legend) => {
                legend.chart.toggleDataVisibility(legendItem.index!);
                legend.chart.update();
                this.percentUiControllerService.buildDatasetFromChartData(this.chart!);
              },
              onHover(e: ChartEvent, legendItem: any, legend: any) {
                if (legendItem === undefined) {
                  legend.chart.canvas.style.cursor = 'default';
                  return;
                }
                legend.chart.canvas.style.cursor = 'pointer';
              }

            }
          }
        },
        plugins: [{
          id: "centerText",
          // @ts-ignore
          beforeDraw: this.drawCenterText.bind(this)
        },
          {
            id: 'customCanvasBackgroundColor',
            beforeDraw: (chart, args, options) => {
              if (options.color === undefined) {
                return;
              }
              const {ctx} = chart;
              ctx.save();
              ctx.globalCompositeOperation = 'destination-over';
              ctx.fillStyle = options.color || '#99ffff';
              ctx.fillRect(0, 0, chart.width, chart.height);
              ctx.restore();
            }
          }
        ]
      }
    );
    this.percentUiControllerService.buildDatasetFromChartData(this.chart!);
  }

  ascendingSort() {

    let x = [this.revenueBreakdownStore.getPieChart().datasets[0].data as number[], this.revenueBreakdownStore.getPieChart().labels as string[]];
    let bgColor = false
    if (this.revenueBreakdownStore.getPieChart().datasets[0].backgroundColor !== undefined) {
      bgColor = true
      x.push(this.revenueBreakdownStore.getPieChart().datasets[0].backgroundColor as string[]);
    }
    let borderColor = false
    if (this.revenueBreakdownStore.getPieChart().datasets[0].borderColor !== undefined) {
      borderColor = true
      x.push(this.revenueBreakdownStore.getPieChart().datasets[0].borderColor as string[]);
    }

    x = GenericSortingService.sortMultipleArrays(x, 0, true)//this.ascendingSort(this.data.datasets[0].data as number[], this.data.labels as string[], this.data.datasets[0].backgroundColor as string[], this.data.datasets[0].borderColor as string[]);

    this.revenueBreakdownStore.getPieChart().datasets[0].data = x[0] as number[];
    this.revenueBreakdownStore.getPieChart().labels = x[1] as string[];
    if (bgColor) {
      this.revenueBreakdownStore.getPieChart().datasets[0].backgroundColor = x[2] as string[];
    }
    if (borderColor) {
      let index = bgColor ? 3 : 2;
      this.revenueBreakdownStore.getPieChart().datasets[0].borderColor = x[index] as string[];
    }
  }

  drawCenterText(chart: Chart<"doughnut">, a: any, b: any) {
    let height = chart.height
    let ctx = chart.ctx;
    let datasetIndex = 0;

    if (this.chart === undefined) return;

    // @ts-ignore
    let innerRadius = chart.getDatasetMeta(0).data[0].innerRadius

    let scaleForText = innerRadius - 10;
    ctx.save();
    let multiplier = 3.25;
    let fontSizeBig = ((scaleForText * multiplier) / 150).toFixed(2);
    let fontSizeMedium = ((scaleForText * multiplier) / 225).toFixed(2);
    let unit = "em";
    let font = "sans-serif";

    let textColor = "#181818";

    let pieChartCenterX = chart.getDatasetMeta(0).data[0].x
    let pieChartCenterY = chart.getDatasetMeta(0).data[0].y


    let text;

    let marginTopPercentageText = Number(fontSizeBig) * 20;
    let marginBottomTitleText = Number(fontSizeBig) * 20;

    let marginYRectangle = -1 * Number(fontSizeBig) * 12 - 5;


    ctx.font = fontSizeBig + unit + " " + font;
    ctx.fillStyle = textColor;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";


    let total = chart.data.datasets[0].data.reduce((sum: number, value: number) => sum + value, 0);


    for (let legendItem in chart!.legend!.legendItems) {
      // @ts-ignore
      if (chart!.legend!.legendItems[legendItem].hidden) {
        // @ts-ignore
        total -= chart.data.datasets[0].data[legendItem]
      }
    }
    // prevent chart from displaying -0.00
    if (total === 0) {
      total = 0
    }


    if (this.selectedIndex === undefined) {

      text = RevenueDoughnutChartComponent.stringToCurrency(total.toString() as string)

    } else {


      text = RevenueDoughnutChartComponent.stringToCurrency(chart.data.datasets[datasetIndex].data[this.selectedIndex].toString())
    }


    let textWidth = ctx.measureText(text).width;
    if (textWidth > scaleForText * 2) {
      let newFontSize = (scaleForText * 2 / textWidth) * Number(fontSizeBig);
      fontSizeBig = newFontSize.toFixed(2);
    }
    ctx.font = fontSizeBig + unit + " " + font;


    ctx.fillText(text, pieChartCenterX, pieChartCenterY);

    ctx.save();

    ctx.font = fontSizeMedium + unit + " " + font;
    ctx.fillStyle = textColor;

    if (this.selectedIndex !== undefined && chart.data.datasets[datasetIndex].data[this.selectedIndex] <= Number(total)) {

      text = "~ " + (chart.data.datasets[datasetIndex].data[this.selectedIndex] / Number(total) * 100).toFixed(2) + "%"
    } else {
      text = "100%"
    }

    ctx.fillText(text, pieChartCenterX, pieChartCenterY + marginTopPercentageText);
    ctx.restore();

    ctx.font = fontSizeMedium + unit + " " + font;
    ctx.fillStyle = textColor;
    if (this.selectedIndex === undefined) {
      text = "Summe"

    } else {

      text = chart.data.labels![this.selectedIndex] as string
    }


    ctx.fillText(text, pieChartCenterX, pieChartCenterY - marginBottomTitleText);

    ctx.restore();

    //
    // Color rectangle in at the bottom
    //
    if (this.selectedIndex === undefined) {
      return;
    }
    ctx.save();

    ctx.font = fontSizeMedium + unit + " " + font;

    let underscoreLength = chart.ctx.measureText(text).width;

    if (this.platform !== Platform.Mozilla && this.platform !== Platform.MozillaMobile) {
      ctx.roundRect(pieChartCenterX - underscoreLength / 2 - 5, pieChartCenterY + marginYRectangle, underscoreLength + 10, 7, [100])

    } else {
      ctx.rect(pieChartCenterX - underscoreLength / 2 - 5, pieChartCenterY + marginYRectangle, underscoreLength + 10, 7)
    }

    ctx.fillStyle = this.chart?.legend?.legendItems![this.selectedIndex as number].fillStyle!
    ctx.fill()
    ctx.restore();
  }
}
